import { useEffect, useState } from "react";
import { AppointmentDto } from "../../../../Api/Model/AppointmentDto.csharp";
import { isObjectEmpty, parseDate } from "../Helpers";
import { AppointmentsApi } from "../../Api/AppointmentsApi";
import { ServiceContainerDto } from "../../../../Api/Model/ServiceContainerDto.csharp";
import { RemoteContactViewModel } from "../../../../Api/Model/RemoteContactViewModel.csharp";
import { GtmService } from "../GtmService";
import { FormStateType } from "../Helpers/formTypes";
import { CenterDto } from "@apiModels/CenterDto.csharp";
import { filterAppointments } from "../Helpers/filterAppointments";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";

const appointmentsApi = new AppointmentsApi();

export const useFilteredAppointmentsDetails = (
  state: FormStateType,
  updateRemoteContact: (remoteContact: RemoteContactViewModel) => void,
  selectedDay: Date | null,
  filteredDoctors: DoctorDto[]
) => {
  const [filteredAppointmentsDetails, setFilteredAppointmentsDetails] =
    useState<AppointmentDto[]>([]);

  const {
    appointmentsDetails,
    loadingAppointmentsDetails,
    error,
    warning,
    failedCenters,
  } = useAppointmentsDetails(state, selectedDay, updateRemoteContact);

  useEffect(() => {
    if (isObjectEmpty(appointmentsDetails)) return;
    if (!selectedDay) return;
    if (!appointmentsDetails[parseDate(selectedDay)]) return;

    const filtered = filterAppointments(
      state,
      appointmentsDetails[parseDate(selectedDay)],
      filteredDoctors
    );

    setFilteredAppointmentsDetails(filtered);
  }, [
    appointmentsDetails,
    state.location,
    state.doctor,
    selectedDay,
    state.relatedServiceSetups,
    state.showRelatedServicesMap,
  ]);

  return {
    filteredAppointmentsDetails,
    loadingAppointmentsDetails,
    error,
    warning,
    failedCenters,
  };
};

const useAppointmentsDetails = (
  state: FormStateType,
  selectedDay: Date | null,
  updateRemoteContact: (remoteContact: RemoteContactViewModel) => void
) => {
  const [appointmentsDetails, setAppointmentsDetails] = useState<{
    [key: string]: AppointmentDto[];
  }>({});
  const [loadingAppointmentsDetailsCount, setLoadingAppointmentsDetailsCount] =
    useState(0);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [failedCenters, setFailedCenters] = useState<CenterDto[]>([]);

  useEffect(() => {
    let cancel = false;
    if (selectedDay) {
      (async () => {
        const parsedDate = parseDate(selectedDay);
        const savedAppointments = appointmentsDetails[parsedDate];
        if (!savedAppointments) {
          setLoadingAppointmentsDetailsCount(
            (previousCount) => previousCount + 1
          );
          const parsedDate = parseDate(selectedDay);
          try {
            const {
              appointments,
              remoteContact,
              cannotLoadSomeAppointments,
              failedCenters,
            } = await getAppointmentDetails(state.service, selectedDay);

            setWarning(cannotLoadSomeAppointments);
            setFailedCenters(failedCenters);

            if (!cancel && !!appointments) {
              setAppointmentsDetails({
                ...appointmentsDetails,
                [parsedDate]: appointments,
              });
              updateRemoteContact(remoteContact);
            }
          } catch (err: any) {
            setError(err.message);
            GtmService.appointmentsLoadingError(
              state.service.name,
              state.specializationName,
              state.location.name,
              err.message
            );
          }
          setLoadingAppointmentsDetailsCount(
            (previousCount) => previousCount - 1
          );
        }
      })();
    }
    return () => {
      cancel = true;
    };
  }, [selectedDay]);

  return {
    appointmentsDetails,
    loadingAppointmentsDetails: loadingAppointmentsDetailsCount > 0,
    error,
    warning,
    failedCenters,
  };
};

export const getAppointmentDetails = async (
  service: ServiceContainerDto,
  date: Date
) => {
  const response = await appointmentsApi.getDetails(
    service.id,
    parseDate(date)
  );
  return {
    cannotLoadSomeAppointments: response.cannotLoadSomeAppointments,
    failedCenters: response.failedCenters,
    appointments: response.appointments.map((a) => ({
      ...a,
      startTime: new Date(a.startTime),
      endTime: new Date(a.endTime),
    })),
    remoteContact: response.remoteContact,
  };
};
